<template>
    <div class="px-6 py-4 bg-minplan-beige">
        <page-title :title="$t('offers.edit')"></page-title>
    </div>

    <jet-form-section @submitted="updateOffer()">
        <template #form>
            <!-- Card Offer-->
            <form-card
                :title="$t('topics.offer')"
                :description="$t('topics.offerDescription')"
            >
                <div class="col-span-6">
                    <label for="title" class="block text-sm font-medium text-gray-700">{{
                        $t("topics.offerTitle")
                    }} <RequiredMark /></label>
                    <input
                        type="text"
                        name="title"
                        id="title"
                        v-model="form.updateBase.title"
                        :placeholder="$t('offers.title')"
                        class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        autofocus
                    />
                    <jet-input-error :message="form.errors.title" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label for="number" class="block text-sm font-medium text-gray-700">{{
                        $t("offers.number")
                    }}</label>
                    <input
                        type="text"
                        name="number"
                        id="number"
                        v-model="form.updateBase.number"
                        :placeholder="$t('offers.number')"
                        class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                    />
                    <jet-input-error
                        :message="form.errors['updateBase.number']"
                        class="mt-2"
                    />
                </div>

                <div class="col-span-6">
                    <label class="block text-sm font-medium text-gray-700">{{ $t("offers.type.label") }}</label>
                    <div class="mt-1 flex items-center space-x-4">
                        <label class="inline-flex items-center">
                            <input type="radio" :value="false" v-model="form.updateBase.is_credit" />
                            <span class="ml-2">{{ $t("offers.type.offer") }}</span>
                        </label>
                        <label class="inline-flex items-center ml-4">
                            <input type="radio" :value="true" v-model="form.updateBase.is_credit" />
                            <span class="ml-2">{{ $t("offers.type.credit") }}</span>
                        </label>
                    </div>
                    <p v-if="form.updateBase.is_credit" class="mt-1 text-xs text-red-500">{{ $t("offers.type.creditHint") }}</p>
                </div>

                <!-- K-3 (UI-Gate Welle 2, welle2-ui-report.md): die beiden
                     Fee-Checkboxen standen unmittelbar unter der Radiogruppe
                     "Art" ohne eigene Feldbeschriftung und wirkten dadurch wie
                     deren Teil. Das Fieldset gibt ihnen eine gemeinsame
                     Gruppenbeschriftung. -->
                <!-- Entscheid 10 (Auftrag 2026-08-16): Im Budget-Modus gibt es die
                     Fee-Fahnen je Offerte fachlich nicht — sie gehen dort
                     weder in die Rechnung noch als Bedienelement ein.
                     AUSGEBLENDET, nicht gesperrt. Die gespeicherten Werte
                     bleiben unangetastet (Entscheid 3) und reisen im
                     Formular unveraendert mit. -->
                <fieldset v-if="!isBudgetBased" class="col-span-6 border border-gray-200 rounded-md p-3">
                    <legend class="px-1 text-sm font-medium text-gray-700">
                        {{ $t("offers.feeFlagsLabel") }}
                    </legend>
                    <div class="col-span-6">
                        <label
                            class="flex items-center space-x-2 text-sm font-medium text-gray-700"
                            for="applyBaseFee"
                        >
                            <input
                                id="applyBaseFee"
                                name="applyBaseFee"
                                v-model="form.updateBase.applyBaseFee"
                                type="checkbox"
                                class="border-gray-300 rounded"
                            />
                            <span>{{ $t("offers.applyBaseFee") }}</span>
                        </label>
                        <jet-input-error
                            :message="form.errors['updateBase.applyBaseFee']"
                            class="mt-2"
                        />
                    </div>

                    <div class="col-span-6 mt-3">
                        <label
                            class="flex items-center space-x-2 text-sm font-medium text-gray-700"
                            for="applyConsultancyFee"
                        >
                            <input
                                id="applyConsultancyFee"
                                name="applyConsultancyFee"
                                v-model="form.updateBase.applyConsultancyFee"
                                type="checkbox"
                                class="border-gray-300 rounded"
                            />
                            <span>{{ $t("offers.applyConsultancyFee") }}</span>
                        </label>
                        <jet-input-error
                            :message="form.errors['updateBase.applyConsultancyFee']"
                            class="mt-2"
                        />
                    </div>
                </fieldset>

                <div class="col-span-6">
                    <label
                        for="costExcl"
                        class="block text-sm font-medium text-gray-700"
                        >{{ $t("offers.cost") }} <RequiredMark /></label
                    >
                    <div class="flex">
                        <span
                            class="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 sm:text-sm mt-1"
                            >CHF</span
                        >
                        <input
                            type="number"
                            step="0.01"
                            name="costExcl"
                            id="costExcl"
                            v-model="form.updateBase.costExcl"
                            :placeholder="$t('offers.cost')"
                            :readonly="hasPriceCalc"
                            :title="hasPriceCalc ? 'Preis kann über die Preisberechnung angepasst werden' : ''"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                            :class="{ 'bg-gray-100 cursor-not-allowed': hasPriceCalc }"
                        />
                    </div>
                    <jet-input-error
                        :message="form.errors['updateBase.costExcl']"
                        class="mt-2"
                    />

                    <!-- Price Calculation Button -->
                    <div class="mt-2 flex items-center gap-2">
                        <button
                            type="button"
                            @click="openPriceCalcModal"
                            class="inline-flex items-center px-3 py-1.5 text-xs font-medium border rounded transition"
                            :class="hasPriceCalc
                                ? 'border-minplan-blue text-minplan-blue bg-blue-50 hover:bg-blue-100'
                                : 'border-gray-400 text-gray-600 bg-white hover:bg-gray-50'"
                        >
                            <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 13h.01M13 13h.01M13 17h.01M17 7h.01M17 13h.01M17 17h.01M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
                            </svg>
                            {{ hasPriceCalc ? $t('offers.priceCalc.editButton') : $t('offers.priceCalc.addButton') }}
                        </button>
                        <button
                            v-if="hasPriceCalc"
                            type="button"
                            @click="removePriceCalc"
                            class="inline-flex items-center px-2 py-1.5 text-xs font-medium border border-red-300 text-red-600 bg-white hover:bg-red-50 rounded transition"
                        >
                            <svg class="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {{ $t('actions.remove') }}
                        </button>
                    </div>
                </div>

                <div class="col-span-6">
                    <label
                        for="taxRate"
                        class="block text-sm font-medium text-gray-700"
                        >{{ $t("offers.taxRate") }}</label
                    >
                    <div class="mt-1 flex">
                        <Dropdown
                            id="taxRate"
                            v-model="form.updateBase.vatCode"
                            :options="taxRates"
                            optionValue="code"
                            optionLabel="name"
                            :placeholder="$t('offers.taxRate')"
                            :showClear="true"
                            class="w-full"
                        />
                    </div>
                    <jet-input-error
                        :message="form.errors['updateBase.vatCode']"
                        class="mt-2"
                    />
                </div>

                <div class="col-span-6">
                    <label class="block text-sm font-medium text-gray-700">
                        {{ $t("offers.file") }} <RequiredMark v-if="$page.props.isOfferDocumentRequired" />
                    </label>
                    <FileDropzone :accept="fileAccept" @file="onDropFile" @reject="onFileRejected">
                        <div class="mt-1 flex items-center">
                            <div class="mt-2 w-full" v-show="fileUploaded">
                                <span class="block">{{ fileUploaded }}</span>
                            </div>

                            <jet-secondary-button
                                class="mt-2 mr-2"
                                type="button"
                                @click.prevent="selectNewFile"
                                v-if="!fileUploaded"
                                >{{ $t("actions.chooseFile") }}
                            </jet-secondary-button>
                            <jet-secondary-button
                                class="mt-2 mr-2"
                                type="button"
                                @click.prevent="clearFileInput"
                                v-if="fileUploaded"
                                >{{ $t("actions.removeFile") }}
                            </jet-secondary-button>

                            <input
                                type="file"
                                class="hidden"
                                ref="file"
                                :accept="fileAccept"
                                @change="updateFileUploaded"
                            />
                        </div>
                    </FileDropzone>
                    <p class="my-3 text-sm text-gray-400">
                        <!-- Der engere Hinweis erscheint nur, wenn die
                             Signaturfrage auch wirklich gestellt ist: auf einem
                             frisch geoeffneten Modal steht `digital_signature`
                             bereits auf "Yes", die Frage aber erst nach der Wahl
                             einer PDF — vorher haette der Hinweis 10 MB genannt,
                             obwohl fuer eine ZIP-Datei 20 MB gelten. -->
                        <span v-if="signatureLimitApplies">{{ $t("offers.fileInfoSigned", { size: maxSizeInMb }) }}</span>
                        <span v-else>{{ $t("offers.fileInfo", { size: maxSizeInMb }) }}</span>
                    </p>
                    <jet-input-error
                        :message="form.errors['updateBase.file']"
                        class="mt-2"
                    />
                </div>

                <div class="col-span-6" v-if="$page.props.offer_document_digital_sign_mode === 'On' && isNewPdfSelected">
                    <label class="block text-sm font-medium text-gray-700">{{ $t("offers.digitalSignatureRequired") }}</label>
                    <div class="mt-1 flex items-center space-x-4">
                        <label class="inline-flex items-center"><input type="radio" value="Yes" v-model="form.digital_signature" /> <span class="ml-2">{{ $t("actions.yes") }}</span></label>
                        <label class="inline-flex items-center ml-4"><input type="radio" value="No" v-model="form.digital_signature" /> <span class="ml-2">{{ $t("actions.no") }}</span></label>
                    </div>
                </div>

                <div class="col-span-6">
                    <label for="bkps" class="block text-sm font-medium text-gray-700">{{
                        $t("offers.bkps")
                    }}</label>
                    <div class="mt-1 flex">
                        <Dropdown
                            filter
                            id="bkps"
                            v-model="form.updateBase.bkp"
                            :options="enrichedBkps"
                            optionValue="value"
                            optionLabel="name"
                            :placeholder="$t('offers.bkp.none')"
                            :showClear="true"
                            class="w-full"
                            @change="handleBkpChange"
                        />
                    </div>
                    <jet-input-error :message="form.errors.bkp" class="mt-2" />
                </div>

                <div class="col-span-6" v-if="form.updateBase.bkp === 'other'">
                    <label
                        for="customBkp"
                        class="block text-sm font-medium text-gray-700"
                        >{{ $t("offers.customBkp") }}</label
                    >
                    <input
                        type="text"
                        name="title"
                        id="title"
                        v-model="form.updateBase.customBkp"
                        :placeholder="$t('offers.customBkp')"
                        class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        autofocus
                    />
                    <jet-input-error :message="form.errors.customBkp" class="mt-2" />
                </div>

                <div class="col-span-6">
                    <label
                        for="suppliers"
                        class="block text-sm font-medium text-gray-700"
                        >{{ $t("offers.supplier") }}</label
                    >
                    <div class="mt-1 flex">
                        <Dropdown
                            filter
                            id="suppliers"
                            v-model="form.updateBase.supplier"
                            :options="alleUnternehmer"
                            optionValue="uuid"
                            optionLabel="name"
                            :placeholder="$t('suppliers.noSupplier')"
                            :showClear="true"
                            class="w-full"
                        />
                    </div>
                    <jet-input-error
                        :message="form.errors['updateBase.supplier']"
                        class="mt-2"
                    />
                    <!-- Unternehmer direkt hier anlegen (Entscheid Peter,
                         31.08.2026) — gleiche Stelle wie in der Themen-Maske. -->
                    <SupplierCreateInline @created="uebernehmeNeuenUnternehmer" />
                </div>

                <!-- R3 Punkt 4: `v-show` statt `v-if` — s. Docblock
                     `ContactPicker.vue` (Reset-Watcher braucht dauerhaftes
                     Mounten). -->
                <div class="col-span-6" v-show="selectedSupplier">
                    <label class="block text-sm font-medium text-gray-700">{{
                        $t("suppliers.contactPerson")
                    }}</label>
                    <ContactPicker
                        :supplier="selectedSupplier"
                        :value="form.updateBase.supplierContact"
                        @input="form.updateBase.supplierContact = $event"
                    />
                    <jet-input-error
                        :message="form.errors['updateBase.supplierContact']"
                        class="mt-2"
                    />
                </div>

                <div class="col-span-6">
                    <label
                        for="comment"
                        class="block text-sm font-medium text-gray-700"
                        >{{ $t("offers.comment") }}</label
                    >
                    <div class="flex">
                        <textarea
                            type="textarea"
                            name="comment"
                            id="comment"
                            :placeholder="$t('offers.comment')"
                            v-model="form.updateBase.comment"
                            class="mt-1 focus:ring-minplan-blue focus:border-minplan-blue block w-full shadow-sm sm:text-sm border-gray-300"
                        />
                    </div>
                    <jet-input-error
                        :message="form.errors['updateBase.comment']"
                        class="mt-2"
                    />
                </div>
            </form-card>
        </template>

        <template #actions>
            <div class="flex justify-end my-4 mx-6">
                <button
                    @click="cancel"
                    type="button"
                    class="inline-block uppercase font-semibold text-xs tracking-widest"
                >
                    {{ $t("actions.cancel") }}
                </button>

                <primary-button
                    type="button"
                    @click="handleSave"
                    :class="{ 'opacity-25': form.processing }"
                    :disabled="form.processing"
                    class="ml-3"
                >
                    <span v-if="form.processing" class="flex items-center gap-1">
                        <svg class="animate-spin h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                        Saving...
                    </span>
                    <span v-else>{{ $t("actions.save") }}</span>
                </primary-button>
            </div>
        </template>
    </jet-form-section>

    <teleport to="body">
        <digital-signature-config 
            v-if="showSignConfigModal"
            class="digital-signature-config-modal-root"
            :buyers="buyers"
            :pdf-base64="pdfBase64"
            :file-name="form.updateBase.title || fileUploaded"
            :auth-user-id="auth_user_id"
            :max-buyer-select="$page.props.offer_document_digital_sign_max_buyer_select"
            @cancel="showSignConfigModal = false"
            @done="finalizeDigitalSignature"
        />

        <price-calculation-modal
            :show="showPriceCalcModal"
            :title="hasPriceCalc ? $t('offers.priceCalc.editButton') : $t('offers.priceCalc.addButton')"
            :units="offerAmountExcludingUnits"
            :initial-rows="form.updateBase.price_calculation_json || []"
            @cancel="showPriceCalcModal = false"
            @save="onPriceCalcSave"
        />
    </teleport>
</template>

<script>
import { defineComponent, ref } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import JetSecondaryButton from "@/Jetstream/SecondaryButton";
import JetFormSection from "@/Jetstream/FormSection";
import JetInput from "@/Jetstream/Input";
import JetInputError from "@/Jetstream/InputError";
import JetLabel from "@/Jetstream/Label";
import PageTitle from "@/Shared/Components/PageTitle";
import { useForm, usePage } from "@inertiajs/inertia-vue3";
import FormCard from "@/Shared/Components/FormCard";
import { useI18n } from "vue-i18n";
import RequiredMark from "@/Shared/Components/RequiredMark";
import DigitalSignatureConfig from "@/Shared/Components/Documents/Modals/DigitalSignatureConfig.vue";
import PriceCalculationModal from "@/Shared/Components/Offers/PriceCalculationModal.vue";
import FileDropzone from "@/Shared/Components/FileDropzone.vue";
import ContactPicker from "@/Shared/Components/Suppliers/ContactPicker.vue";
import SupplierCreateInline from "@/Shared/Components/Suppliers/SupplierCreateInline.vue";

// Fix Welle 4.5 UI-Gate, Nachprüfung f6c76cbb: s. Docblock/Kommentar bei
// `fileAccept` in `CreateOffer.vue`.
const OFFER_FILE_ACCEPT =
    ".pdf,.zip,application/pdf,application/zip,application/x-zip-compressed";

export default defineComponent({
    props: ["suppliers", "offer", "updateLink", "bkps", "taxRates", "buyers", "auth_user_id", "offer_amount_excluding_units", "max_signed_offer_upload_size", "feeCalculation"],
    inheritAttrs: false,
    layout: MainLayout,
    emits: ["closeModal"],
    components: {
        PageTitle,
        PrimaryButton,
        JetFormSection,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        FormCard,
        RequiredMark,
        DigitalSignatureConfig,
        PriceCalculationModal,
        FileDropzone,
        ContactPicker,
        SupplierCreateInline,
    },
    computed: {
        // Entscheid 10 (Auftrag 2026-08-16): fehlt die Prop, bleibt die Maske
        // wie bisher — Offerten-Modus ist der Default.
        isBudgetBased() {
            return this.feeCalculation ? this.feeCalculation.isBudgetBased : false;
        },
        // Befund H-6: statt die Inertia-Prop `suppliers` per `push()` zu
        // mutieren, fuehrt eine lokale Liste die frisch angelegten
        // Unternehmer — `alleUnternehmer` fuehrt beides zusammen.
        alleUnternehmer() {
            return [...(this.suppliers || []), ...this.zusaetzlicheUnternehmer];
        },
        selectedSupplier() {
            return this.alleUnternehmer.find((supplier) => supplier.uuid === this.form.updateBase.supplier) ?? null;
        },
        // Zu signierende Offerten unterliegen einer engeren Grenze
        // (`core.max_signed_offer_upload_size`, als Seiten-Prop geliefert) —
        // ohne Signatur bleibt `max_offer_upload_size` massgeblich. Dieselbe
        // Weiche steht serverseitig in StoreOfferRequest/UpdateOfferRequest;
        // hier ist sie nur die frueher greifende Rueckmeldung, nicht der
        // Schutz.
        // Die engere Grenze gilt nur, wenn die Signaturfrage im Formular
        // ueberhaupt gestellt ist (Modus aktiv UND eine PDF gewaehlt) und mit
        // "Ja" beantwortet wurde.
        signatureLimitApplies() {
            return (
                this.$page.props.offer_document_digital_sign_mode === "On" &&
                // Dieses Modal fuehrt `isNewPdfSelected`, nicht
                // `isPdfSelected` wie seine beiden Geschwister — die Frage ist
                // hier "wurde eine NEUE PDF gewaehlt", nicht "ist eine PDF
                // gewaehlt". Die Vorlage aus `CreateOffer` traegt den anderen
                // Namen; das Template darunter (`:244`) nutzt seit jeher den
                // richtigen.
                this.isNewPdfSelected &&
                this.form.digital_signature === "Yes"
            );
        },
        maxBytesForSelection() {
            const unsigned =
                this.$page.props.branding?.max_offer_upload_size || 20 * 1024 * 1024;
            // `max_signed_offer_upload_size` ist ein SEITEN-Prop der
            // Modal-Route und kommt ueber `v-bind` in `ComponentModal` als
            // Komponenten-Prop an — NICHT ueber `$page.props`, das hier die
            // Gastgeberseite ist.
            const signed = this.max_signed_offer_upload_size || 10 * 1024 * 1024;
            return this.signatureLimitApplies ? signed : unsigned;
        },
        maxSizeInMb() {
            return Math.round(this.maxBytesForSelection / 1024 / 1024);
        },
        // R3 Punkt 7 / Fix Welle 4.5 UI-Gate (Nachprüfung f6c76cbb): s.
        // Docblock/Kommentar bei `fileAccept` in `CreateOffer.vue` — jetzt
        // IMMER dieselbe Offerten-Whitelist, unabhängig von
        // `form.digital_signature`.
        fileAccept() {
            return OFFER_FILE_ACCEPT;
        },
        isNewPdfSelected() {
            if (!this.form.changedFile || !this.fileUploaded) return false;
            return this.fileUploaded.toLowerCase().endsWith('.pdf');
        },
        hasPriceCalc() {
            return this.form.updateBase.price_calculation_json &&
                this.form.updateBase.price_calculation_json.length > 0;
        },
        offerAmountExcludingUnits() {
            return this.offer_amount_excluding_units || ["Stk.", "m²", "m", "Std."];
        },
    },
    watch: {
        // Fix Welle 4.5 UI-Gate (welle45-ui-report.md, blockierender Befund):
        // ein reiner Wertwechsel-Watcher auf `isNewPdfSelected` feuert nicht,
        // wenn direkt (ohne vorherige PDF-Auswahl) eine Nicht-PDF-Datei
        // gewählt wird. Der Reset passiert deshalb jetzt deterministisch in
        // `updateFileUploaded()` bei jeder Dateiauswahl — s. Kommentar dort.
        // R3 Punkt 4: der Unternehmer-Wechsel-Reset der Kontaktperson wandert
        // in `ContactPicker.vue` — kein eigener Watcher mehr hier.
    },
    methods: {
        /**
         * Der frisch angelegte Unternehmer wandert in die Auswahlliste und ist
         * gleich gewählt — ohne Neuladen, damit die bereits erfassten Angaben
         * dieser Offerte erhalten bleiben.
         */
        uebernehmeNeuenUnternehmer(supplier) {
            this.zusaetzlicheUnternehmer.push(supplier);
            this.form.updateBase.supplier = supplier.uuid;
            this.form.supplier_contact_uuid = null;
        },
        cancel() {
            this.$emit("closeModal");
        },
        openPriceCalcModal() {
            this.showPriceCalcModal = true;
        },
        onPriceCalcSave(rows) {
            this.showPriceCalcModal = false;
            this.form.updateBase.price_calculation_json = rows;
            // Set costExcl to grand total
            const total = rows.reduce((sum, r) => sum + (r.total || 0), 0);
            this.form.updateBase.costExcl = Math.round(total * 100) / 100;
        },
        removePriceCalc() {
            this.form.updateBase.price_calculation_json = null;
            this.form.updateBase.costExcl = null;
        },
        handleSave() {
            this.form.errors.title = null;
            this.form.errors['updateBase.costExcl'] = null;

            if (!this.form.updateBase.title) {
                this.form.errors.title = "Title is required.";
                return;
            }

            if ((this.form.updateBase.costExcl === null || this.form.updateBase.costExcl === undefined || this.form.updateBase.costExcl === "") || parseFloat(this.form.updateBase.costExcl) < 0) {
                this.form.errors['updateBase.costExcl'] = this.$t("offers.priceCalc.errorCostZero");
                return;
            }

            if (this.hasPriceCalc) {
                const total = this.form.updateBase.price_calculation_json.reduce((sum, r) => sum + (r.total || 0), 0);
                const roundedTotal = Math.round(total * 100) / 100;
                if (Math.abs(parseFloat(this.form.updateBase.costExcl) - roundedTotal) > 0.01) {
                    this.$notify({ group: "error", title: this.$t("actions.error"), text: this.$t("offers.priceCalc.errorMismatch") });
                    return;
                }
            }

            if (this.$refs.file && this.$refs.file.files[0]) {
                const fsize = this.$refs.file.files[0].size;
                const maxBytes = this.maxBytesForSelection;

                if (fsize > maxBytes) {
                    this.$notify({
                        group: "error",
                        title: "actions.error",
                        // Der Toast kuerzt bei 100 Zeichen und laeuft auf
                        // einem Timer ab — die Meldung nennt darum knapp die
                        // Grenze, statt in einem abgeschnittenen Satz zu
                        // enden.
                        text: this.signatureLimitApplies
                            ? this.$t("offers.signedFileTooBig", {
                                  size: this.maxSizeInMb,
                              })
                            : "offers.fileTooBig",
                    });
                    return;
                }

                const fileExt = this.$refs.file.files[0].name.split('.').pop().toLowerCase();
                
                if (this.form.digital_signature === 'Yes' && fileExt !== 'pdf') {
                    this.$notify({ group: "error", title: "actions.error", text: "offers.digitalSignatureRequiresPdf" });
                    return;
                }

                this.form.updateBase.file = this.$refs.file.files[0];

                if (this.form.digital_signature === 'Yes') {
                    this.loadPdfBase64();
                    this.showSignConfigModal = true;
                    return;
                }
            } else if (this.form.digital_signature === 'Yes') {
                this.form.digital_signature = 'No';
            }

            this.updateOffer();
        },
        updateOffer() {
            this.form.updateBase.file = this.$refs.file.files[0] ?? null;
            this.form.post(this.updateLink, {
                preserveScroll: true,
                onSuccess: () =>
                    this.$notify({
                        group: "success",
                        title: "actions.success",
                        text: "offers.updated",
                    }),
            });
        },
        loadPdfBase64() {
            const file = this.$refs.file.files[0];
            const reader = new FileReader();
            reader.onload = () => {
                const dataUrl = reader.result;
                if (typeof dataUrl === 'string') {
                    this.pdfBase64 = dataUrl.split(',')[1];
                    this.pdfLoaded = true;
                }
            };
            reader.readAsDataURL(file);
        },
        b64toFile(b64Data, contentType='', sliceSize=512) {
            const byteCharacters = atob(b64Data);
            const byteArrays = [];
            for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
                const slice = byteCharacters.slice(offset, offset + sliceSize);
                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) byteNumbers[i] = slice.charCodeAt(i);
                byteArrays.push(new Uint8Array(byteNumbers));
            }
            return new File(byteArrays, this.form.updateBase.title + ".pdf", { type: contentType });
        },
        finalizeDigitalSignature(payload) {
            this.showSignConfigModal = false;
            this.form.updateBase.digital_sign_json = payload.digital_sign_json;
            if (payload.pdf_base64_signed) {
                this.form.updateBase.file = this.b64toFile(payload.pdf_base64_signed, 'application/pdf');
            }
            this.updateOffer();
        },
        selectNewFile() {
            this.$refs.file.click();
        },
        clearFileInput() {
            if (this.$refs.file?.value) {
                this.$refs.file.value = null;
            }

            this.fileUploaded = null;
            this.form.changedFile = true;
        },
        updateFileUploaded() {
            const file = this.$refs.file.files[0];

            if (!file) return;

            this.fileUploaded = file.name;
            this.form.changedFile = true;

            // Fix Welle 4.5 UI-Gate: bei jeder (auch der ersten) Auswahl
            // einer Nicht-PDF-Datei deterministisch auf "No" zurücksetzen —
            // s. Kommentar beim `watch`-Block oben.
            if (!file.name.toLowerCase().endsWith('.pdf')) {
                this.form.digital_signature = 'No';
            }
        },
        onDropFile(file) {
            const dt = new DataTransfer();
            dt.items.add(file);
            this.$refs.file.files = dt.files;
            this.$refs.file.dispatchEvent(new Event("change", { bubbles: true }));
        },
        // W2 (Fix-Runde 1): s. Kommentar in `CreateOffer.vue`.
        onFileRejected() {
            this.$notify({
                group: "error",
                title: "actions.error",
                text: "offers.fileTypeInvalid",
            });
        },
        handleBkpChange() {
            if (this.form.updateBase.bkp !== "other") {
                this.form.updateBase.customBkp = null;
            }
        },
    },
    setup(props) {
        const fileUploaded = ref(props.offer.filename);
        const { t } = useI18n();
        const { props: pageProps } = usePage();
        const dsMode = pageProps.value.offer_document_digital_sign_mode;

        const form = useForm({
            changedFile: false,
            updateBase: {
                title: props.offer.title,
                costExcl: props.offer.netCost === null || props.offer.netCost === undefined ? null : Math.abs(props.offer.netCost),
                is_credit: props.offer.is_credit,
                number: props.offer.number,
                comment: props.offer.comment,
                file: null,
                supplier: props.offer.supplier_uuid,
                // Thema 9 Paket c: IMMER mitsenden (auch `null`) — die
                // Feature-Ebene unterscheidet `has()` (Schlüssel vorhanden,
                // leer = Kontakt entfernen) von fehlendem Schlüssel (Kontakt
                // behalten, `UpdateOfferFeature:93-99`). Ein bedingt
                // weggelassenes Feld würde einen bestehenden Kontakt nie
                // wieder löschbar machen.
                supplierContact: props.offer.supplier_contact_uuid ?? null,
                bkp: props.offer.bkp,
                customBkp: props.offer.customBkp,
                vatCode: props.offer.vatCode,
                digital_sign_json: props.offer.digital_sign_json,
                price_calculation_json: props.offer.price_calculation_json || null,
                applyBaseFee: props.offer.apply_base_fee ?? true,
                applyConsultancyFee: props.offer.apply_consultancy_fee ?? true,
            },
            digital_signature: dsMode === 'Off' ? "No" : "Yes",
        });

        const enrichedBkps = [
            ...props.bkps.map((bkp) => ({
                value: bkp,
                name: t(`offers.bkp.N${bkp.replace(".", "_")}`),
            })),
            { value: "other", name: t("offers.bkp.other") },
        ];

        return { form, fileUploaded, enrichedBkps };
    },
    data() {
        return {
            showSignConfigModal: false,
            pdfLoaded: false,
            pdfBase64: null,
            showPriceCalcModal: false,
            // Befund H-6: frisch angelegte Unternehmer landen hier statt in
            // der Prop `suppliers` (s. `alleUnternehmer`/`uebernehmeNeuenUnternehmer`).
            zusaetzlicheUnternehmer: [],
        }
    }
});
</script>

<style scoped></style>
